/**
 * Device-local persistence for the learner's chosen track (issue #283). No
 * `/v1/me` profile field exists for this yet, so one selection per device is
 * the MVP scope for both guest and authed learners. Mirrors the
 * safe-localStorage pattern already duplicated in lib/auth/storage.ts and
 * lib/training/sessionSnapshot.ts.
 */
const STORAGE_KEY = 'simbay.trackSelection';

export interface TrackSelection {
  ecosystem: string;
  route: string;
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // localStorage can throw (private mode, disabled cookies) — degrade to
    // no persistence rather than crashing the app.
    return null;
  }
}

function isTrackSelection(value: unknown): value is TrackSelection {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as TrackSelection).ecosystem === 'string' &&
    typeof (value as TrackSelection).route === 'string'
  );
}

export function readTrackSelection(): TrackSelection | null {
  const raw = safeLocalStorage()?.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isTrackSelection(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeTrackSelection(selection: TrackSelection): void {
  safeLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(selection));
}
